import { CoRetrieveDocumentId } from "../../components/generic/CoRetrieveDocumentId";
import React, { useEffect, useState } from "react";
import { DocumentNotValidError } from "../../components/generic/DocumentNotValidError";
import { AllCoObjectsSelector } from "../../components/generic/AllCoObjectsSelector";
import { ObjectsListUpdatesPanel } from "../../components/document/ObjectsListUpdatesPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { CoObjectItem } from "../../components/interfaces/CoObject";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { TableUpdateStrategy } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { IconButton, IContextualMenuItem, TeachingBubble } from "@fluentui/react";
import { launchRefreshElaboration } from "../../utils/helpers/ElaborationHelper";

export const ObjectsListPage: React.FunctionComponent = () => {
  return (
    <CoRetrieveDocumentId>
      {(coObject) =>
        coObject?.documentId ? (
          <AllCoObjectsSelector documentId={coObject.documentId}>
            {(coObjectsArray) => (
              <ObjectsListUpdatesPanel
                documentId={coObject.documentId}
                documentCoObjects={coObjectsArray.filter(
                  (coObject) => coObject.info.objectType != "DOCUMENT_PART_INCLUDE"
                )}
                updateSelectionOnly={false}
                showObjectIcons={true}
                mainHeaderTitle={formatMessage("cof.objectslist.objectslist")}
                footerComponents={ObjectsListElaborationLaunchButton}
                page={"objectList"}
              />
            )}
          </AllCoObjectsSelector>
        ) : (
          <DocumentNotValidError />
        )
      }
    </CoRetrieveDocumentId>
  );
};

interface UpdateStrategy {
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
  teachingBubbleVisible: boolean;
}

export const ObjectsListElaborationLaunchButton = (props: {
  selectedObjects: Map<string, CoObjectItem>;
  username: string;
}) => {
  const { selectedObjects, username } = props;

  useEffect(() => {
    // Resetta l'opzione di aggiornamento a "update" quando cambia la selezione degli oggetti
    setState({ ...state, tableUpdateType: "update" });
  }, [atLeastOneTableOrChartPresent(selectedObjects)]);

  const tableUpdateOptions: IContextualMenuItem[] = [
    {
      key: "update",
      text: formatMessage("cof.base.update"),
      depthLevel: "SHALLOWEST",
    },
    {
      key: "fullReset",
      text: formatMessage("cof.updates.fullreset"),
      depthLevel: "DEEPEST",
    },
    {
      key: "keepAllChanges",
      text: formatMessage("cof.updates.keepallchanges"),
      depthLevel: "DEEPEST",
    },
  ];

  const infoId = "ObjectsListPageInfoId";

  const [state, setState] = useState<UpdateStrategy>({ tableUpdateType: "update", teachingBubbleVisible: false });

  const setTableUpdateType = (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => {
    setState((s) => ({ ...s, tableUpdateType }));
  };

  function atLeastOneTableOrChartPresent(map: Map<string, CoObjectItem>): boolean {
    let allAreTablesOrCharts = false;
    map.forEach((item) => {
      if (item.info.objectType === "TABLE" || item.info.objectType === "CHART") {
        allAreTablesOrCharts = true;
      }
    });
    return allAreTablesOrCharts;
  }

  return (
    <>
      {state.teachingBubbleVisible && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          hasCloseButton={true}
          target={`#${infoId}`}
          closeButtonAriaLabel="Close"
          onDismiss={() => setState({ ...state, teachingBubbleVisible: false })}
          headline={formatMessage("cof.documentpartupdate.teachingbubble.headline")}
        >
          <ul>
            <li>
              <b>{formatMessage("cof.base.update")}</b>:
              {formatMessage("cof.objectlistupdate.teachingbubble.update.description")}
            </li>
            <li>
              <b>{formatMessage("cof.updates.fullreset")}</b>:
              {formatMessage("cof.objectlistupdate.teachingbubble.fullreset.description")}
            </li>
            <li>
              <b>{formatMessage("cof.updates.keepallchanges")}</b>:
              {formatMessage("cof.objectlistupdate.teachingbubble.keepallchanges.description")}
            </li>
          </ul>
        </TeachingBubble>
      )}
      <PrimaryButton
        id="update_button"
        disabled={selectedObjects.size == 0}
        className={ButtonVariants.primary}
        styles={{
          label: {
            whiteSpace: "nowrap",
          },
          root: { float: "right" },
          splitButtonContainer: { float: "right" },
        }}
        onClick={() =>
          launchRefreshElaboration(username, Array.from(selectedObjects.values()), undefined, state.tableUpdateType)
        }
        text={tableUpdateOptions.find((opt) => state.tableUpdateType === opt.key).text}
        split={atLeastOneTableOrChartPresent(selectedObjects)}
        menuProps={
          atLeastOneTableOrChartPresent(selectedObjects) && {
            items: tableUpdateOptions,
            isBeakVisible: false,
            onItemClick: (_, opt) => {
              const newStrategyType = opt.key as TableUpdateStrategy["typeUpdate"];
              setTableUpdateType(newStrategyType);
            },
          }
        }
      />
      <IconButton
        id={infoId}
        hidden={!atLeastOneTableOrChartPresent(selectedObjects)}
        aria-label="Info"
        iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
        styles={{
          label: {
            whiteSpace: "nowrap",
          },
          root: { float: "right" },
          splitButtonContainer: { float: "right" },
        }}
        disabled={selectedObjects.size == 0}
        onClick={() => setState({ ...state, teachingBubbleVisible: !state.teachingBubbleVisible })}
      />
    </>
  );
};
